/**
 * Business Credit Builder Page - Fundability System
 * Comprehensive page showcasing the Legacy Fundability System
 */

import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  TrendingUp, Target, Users, Shield, CheckCircle, ArrowRight,
  Star, Play, CreditCard, Building2, Search, BarChart3, FileText,
  Eye, Briefcase, Clock, DollarSign, Award, Zap
} from "lucide-react";

// Hero background - diverse team building success
const HERO_BG = "https://customer-assets.emergentagent.com/job_mirror-build-7/artifacts/oxxz86w4_office-people-happy-teamwork-with-building-blocks-problem-solving-solution-educational-diversity-employees-game-with-success-celebration-support-as-graphic-designer.jpg";

// Key benefits of Fundability
const keyBenefits = [
  {
    icon: TrendingUp,
    title: "Improve Your Business Fundability",
    description: "Boost your business's fundability to access better funding options. Identify and fix issues to qualify for more favorable credit lines & terms."
  },
  {
    icon: CreditCard,
    title: "Unlock High-Limit Business Credit Lines",
    description: "Secure high-limit business credit lines in your business EIN and improve your business credit profile for better loan options."
  },
  {
    icon: Building2,
    title: "Access Every Credible Lending Option",
    description: "Increase your funding options with access to every credible business lending option, including both alternative & traditional lenders."
  },
  {
    icon: Users,
    title: "On-Demand Concierge Support",
    description: "Our expert Business Credit Advisors offer personalized support to help overcome obstacles & secure the best financing terms."
  },
];

// Platform features
const platformFeatures = [
  {
    id: "foundation",
    icon: Search,
    label: "FUNDABILITY FOUNDATION™",
    title: "Discover the small changes that can have a BIG impact on approvals",
    description: "Find out why you're being denied and what you can do about it to become irresistible to lenders.",
    color: "#48d480",
  },
  {
    id: "prequalified",
    icon: CheckCircle,
    label: "PRE-QUALIFIED FINANCING",
    title: "See the business credit lines and loans you qualify for",
    description: "Find out which programs you're eligible for now, which ones you aren't, and get guidance on securing approvals, improving your rates and terms, and accessing larger loan amounts.",
    color: "#48d480",
  },
  {
    id: "builder",
    icon: Briefcase,
    label: "BUSINESS CREDIT BUILDER",
    title: "Take the guesswork out of getting the business credit you need",
    description: "Get a clear, step-by-step plan to secure the best credit options and guidance through the approval process, ensuring favorable terms and high credit limits.",
    color: "#e8821c",
  },
  {
    id: "score",
    icon: BarChart3,
    label: "THE FUNDABILITY SCORE™",
    title: "Know your Fundability Score™ and boost approvals",
    description: "The only software that integrates with lenders' secret credit reports and key factors to generate a Fundability Score™, revealing your business's current fundability status.",
    color: "#48d480",
  },
  {
    id: "explorer",
    icon: Target,
    label: "FUNDING EXPLORER™",
    title: "Find the right business loan at the right time",
    description: "See all available business loans at a glance. Get detailed insights into which financing programs you qualify for to help you make informed decisions.",
    color: "#e8821c",
  },
  {
    id: "manager",
    icon: FileText,
    label: "FUNDING MANAGER™",
    title: "Track and manage your business credit accounts effortlessly",
    description: "Keep an organized record of your credit-building progress and track loan applications all in one place.",
    color: "#48d480",
  },
  {
    id: "bureau",
    icon: Eye,
    label: "BUREAU INSIGHTS™",
    title: "Take control of the data lenders use to make decisions",
    description: "Access the same information that lenders see, allowing you to identify and correct errors that may be hindering your financing opportunities.",
    color: "#e8821c",
  },
];

// Testimonials
const testimonials = [
  {
    quote: "The business credit building process unlocked more funding options than I ever imagined, opening new possibilities for my business!",
    author: "Cheryl R.",
    role: "Small Business Owner",
    rating: 5,
  },
  {
    quote: "Within 3 to 4 months I was able to secure $50,000 in funding for my business! I will definitely be referring all my clients.",
    author: "Brian H.",
    role: "Entrepreneur",
    rating: 5,
  },
  {
    quote: "I was able to establish a business credit profile which enabled me to apply for funding without using my SSN, eliminating my personal liability!",
    author: "Edward A.",
    role: "Business Owner",
    rating: 5,
  },
];

// Stats
const stats = [
  { value: "1,000+", label: "Lender Partners", icon: Building2 },
  { value: "$50M+", label: "Funding Secured", icon: DollarSign },
  { value: "10,000+", label: "Businesses Helped", icon: Users },
  { value: "24-48hrs", label: "Fast Approvals", icon: Clock },
];

export default function BusinessCreditBuilder() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".fade-up").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="business-credit-builder-page">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden pt-32 md:pt-40">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-[#0f2d52]/80" />
        
        <div className="container relative z-10 py-16 md:py-24">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#48d480]/20 border border-[#48d480]/40 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#48d480] animate-pulse" />
              <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#48d480] text-xs font-semibold tracking-wide">
                Legacy Fundability System
              </span>
            </div>
            
            <h1
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6"
            >
              Give Your Business the
              <br />
              <span className="text-[#48d480]">Credit it Deserves</span>
            </h1>
            
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-white/80 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl mx-auto"
            >
              Get high-limit business credit lines and access to 1,000+ lenders with the Fundability System. Build stronger business credit and unlock better funding options.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a
                href="https://app.trulli.ai/marketplace/legacytriangle"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center justify-center gap-2 text-base px-8 py-4"
              >
                Get Started Today <ArrowRight size={18} />
              </a>
              <Link
                to="/contact"
                className="btn-outline-white flex items-center justify-center gap-2 text-base px-8 py-4"
              >
                <Play size={16} /> Schedule a Demo
              </Link>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                    <Icon size={20} className="text-[#48d480] mx-auto mb-2" />
                    <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-white font-black text-xl">{stat.value}</div>
                    <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-xs">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* EIN Business Credit Stats Section */}
      <section className="py-20 bg-gradient-to-b from-[#0a1f3a] to-[#0f2d52]">
        <div className="container">
          {/* First Block - Only 15% Stats */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-16 lg:mb-20 fade-up">
            {/* Pie Chart Visual */}
            <div className="relative flex justify-center order-2 lg:order-1">
              <div className="relative w-56 h-56 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-80 lg:h-80">
                {/* Pie chart background */}
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                  {/* 85% Gray slice */}
                  <circle
                    cx="50" cy="50" r="45"
                    fill="transparent"
                    stroke="#6b7280"
                    strokeWidth="20"
                    strokeDasharray="240.33 283.53"
                    className="drop-shadow-lg"
                  />
                  {/* 15% Green slice */}
                  <circle
                    cx="50" cy="50" r="45"
                    fill="transparent"
                    stroke="#48d480"
                    strokeWidth="20"
                    strokeDasharray="42.41 283.53"
                    strokeDashoffset="-240.33"
                    className="drop-shadow-lg"
                  />
                </svg>
                {/* Center labels */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-gray-300 text-lg font-bold">85%</div>
                    <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-400 text-xs">No Established<br/>Business Credit</div>
                  </div>
                </div>
                {/* 15% label */}
                <div className="absolute top-4 right-8 text-center">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#48d480] text-2xl font-black">15%</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white text-xs">Built EIN<br/>Credit Profile</div>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="order-1 lg:order-2">
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 leading-tight"
              >
                Only <span className="text-[#48d480]">15%</span> of U.S. Businesses
              </h2>
              <h3
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-xl sm:text-2xl md:text-3xl font-bold text-white/80 mb-6 sm:mb-8"
              >
                Have Real <span className="text-[#48d480]">EIN Business Credit</span>
              </h3>
              
              {/* Stats boxes */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-5 text-center">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-xl sm:text-2xl md:text-3xl font-black text-white">36,000,000</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-xs sm:text-sm">Total U.S. Businesses</div>
                </div>
                <div className="bg-[#48d480]/10 border border-[#48d480]/30 rounded-xl p-3 sm:p-5 text-center">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-xl sm:text-2xl md:text-3xl font-black text-[#48d480]">5,400,000</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-xs sm:text-sm">With EIN Credit Build-Out</div>
                </div>
              </div>
              
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/70 text-lg leading-relaxed"
              >
                <span className="text-white font-semibold">30.6 million businesses</span> are operating without a fully developed business credit structure.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-8" />

          {/* Second Block - EIN Credit Advantage */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center fade-up">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-xl sm:text-2xl md:text-3xl font-bold text-white/80 mb-2"
              >
                Businesses with EIN Credit
              </h2>
              <h3
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6 sm:mb-8"
              >
                Reap the <span className="text-[#48d480]">EIN Business Credit Advantage</span>
              </h3>
              
              {/* Benefits list */}
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#48d480]/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={20} className="text-[#48d480] sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-1 sm:gap-2 mb-1">
                      <span style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#48d480] text-xl sm:text-2xl font-black">10X</span>
                      <span style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-white font-bold text-sm sm:text-base">Higher Credit Limits</span>
                    </div>
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-xs sm:text-sm">
                      Businesses with EIN credit profiles are <span className="text-white font-semibold">10 times more likely</span> to receive much higher credit limits.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#48d480]/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={20} className="text-[#48d480] sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-1 sm:gap-2 mb-1">
                      <span style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#48d480] text-xl sm:text-2xl font-black">50%</span>
                      <span style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-white font-bold text-sm sm:text-base">More Likely to Be Approved for Loans</span>
                    </div>
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-xs sm:text-sm">
                      50% higher chance of approval for <span className="text-white font-semibold">business loans</span> and lines of credit compared to those without EIN credit built-out.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#48d480]/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={20} className="text-[#48d480] sm:w-6 sm:h-6" />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-1 sm:gap-2 mb-1">
                      <span style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#48d480] text-xl sm:text-2xl font-black">2X</span>
                      <span style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-white font-bold text-sm sm:text-base">Greater Trust from Lenders & Suppliers</span>
                    </div>
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 text-xs sm:text-sm">
                      Businesses <span className="text-white font-semibold">reap double</span> the trust from lenders and suppliers, leading to better terms and rates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Pie Chart Visual */}
            <div className="relative flex justify-center order-1 lg:order-2">
              <div className="relative w-56 h-56 sm:w-64 sm:h-64 md:w-72 md:h-72 lg:w-80 lg:h-80">
                {/* Pie chart */}
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                  <circle
                    cx="50" cy="50" r="45"
                    fill="transparent"
                    stroke="#6b7280"
                    strokeWidth="20"
                    strokeDasharray="240.33 283.53"
                    className="drop-shadow-lg"
                  />
                  <circle
                    cx="50" cy="50" r="45"
                    fill="transparent"
                    stroke="#48d480"
                    strokeWidth="20"
                    strokeDasharray="42.41 283.53"
                    strokeDashoffset="-240.33"
                    className="drop-shadow-lg"
                  />
                </svg>
                {/* Center labels */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-gray-300 text-lg font-bold">85%</div>
                    <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-400 text-xs">Missing Out on<br/>EIN Credit Benefits</div>
                  </div>
                </div>
                {/* 15% label */}
                <div className="absolute top-4 right-8 text-center">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#48d480] text-2xl font-black">15%</div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white text-xs">Built EIN<br/>Credit Profile</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12 sm:mt-16 fade-up px-4">
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 mb-4 sm:mb-6 text-base sm:text-lg">
              Don't be part of the 85%. Build your EIN business credit today.
            </p>
            <a
              href="https://app.trulli.ai/marketplace/legacytriangle"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2 text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
            >
              Start Building Your Business Credit <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#48d480]" />
                <span className="section-label">Watch The Demo</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-bold text-white mb-6"
              >
                See How Fundability <span className="text-[#48d480]">Works</span>
              </h2>
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/70 text-lg leading-relaxed mb-6"
              >
                Watch our quick demo to see how the Fundability System can help you build business credit, unlock funding options, and grow your business faster than ever.
              </p>
              <div className="space-y-4">
                {[
                  "Step-by-step walkthrough of the platform",
                  "See real examples of funding success",
                  "Learn how to maximize your fundability score",
                  "Discover which loans you qualify for"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={18} className="text-[#48d480]" />
                    <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/80 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="fade-up">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-[#48d480]/30">
                <div className="aspect-video">
                  <iframe
                    title="Fundability Demo Video"
                    src="https://player.vimeo.com/video/1119582735?h=cf1e0057c7"
                    className="w-full h-full"
                    frameBorder="0"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-14 fade-up">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Why Fundability?</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              Fundability is <span className="text-[#48d480]">Your Solution</span>
            </h2>
            <p
              style={{ fontFamily: "'Inter', sans-serif" }}
              className="text-gray-500 max-w-2xl mx-auto"
            >
              Everything you need to build business credit, qualify for better funding, and grow your business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {keyBenefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={benefit.title}
                  className="bg-white rounded-xl p-8 border border-gray-100 hover:border-[#48d480] hover:shadow-xl transition-all duration-300 fade-up"
                  style={{ transitionDelay: `${idx * 100}ms` }}
                >
                  <div className="w-14 h-14 bg-[#48d480] rounded-xl flex items-center justify-center mb-5">
                    <Icon size={28} className="text-white" />
                  </div>
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-[#0f2d52] font-bold text-lg mb-3"
                  >
                    {benefit.title}
                  </h3>
                  <p
                    style={{ fontFamily: "'Inter', sans-serif" }}
                    className="text-gray-500 text-sm leading-relaxed"
                  >
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Platform Features Section */}
      <section className="py-20 bg-[#f8fafc]">
        <div className="container">
          <div className="text-center mb-14 fade-up">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Platform Features</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              Powerful Tools to Build <span className="text-[#48d480]">Business Credit</span>
            </h2>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {platformFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              const isEven = idx % 2 === 0;
              
              return (
                <div
                  key={feature.id}
                  className={`grid lg:grid-cols-2 gap-6 sm:gap-8 items-center bg-white rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-gray-100 hover:shadow-lg transition-all fade-up ${
                    isEven ? "" : "lg:flex-row-reverse"
                  }`}
                >
                  <div className={isEven ? "lg:order-1" : "lg:order-2"}>
                    <div
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-3 sm:mb-4"
                      style={{ backgroundColor: `${feature.color}20` }}
                    >
                      <Icon size={12} className="sm:w-[14px] sm:h-[14px]" style={{ color: feature.color }} />
                      <span
                        style={{ fontFamily: "'Inter', sans-serif", color: feature.color }}
                        className="text-[10px] sm:text-xs font-bold tracking-wider"
                      >
                        {feature.label}
                      </span>
                    </div>
                    <h3
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                      className="text-xl sm:text-2xl font-bold text-[#0f2d52] mb-3 sm:mb-4"
                    >
                      {feature.title}
                    </h3>
                    <p
                      style={{ fontFamily: "'Inter', sans-serif" }}
                      className="text-gray-600 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6"
                    >
                      {feature.description}
                    </p>
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-[#48d480] font-bold text-sm sm:text-base hover:gap-3 transition-all"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      Learn More <ArrowRight size={16} />
                    </Link>
                  </div>
                  
                  <div className={`${isEven ? "lg:order-2" : "lg:order-1"} flex justify-center`}>
                    <div
                      className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl sm:rounded-2xl flex items-center justify-center"
                      style={{ backgroundColor: `${feature.color}15` }}
                    >
                      <Icon className="w-12 h-12 sm:w-14 sm:h-14" style={{ color: feature.color }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expert Advice CTA */}
      <section className="py-20 bg-[#0f2d52]">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-up">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-[#48d480]" />
                <span className="section-label">Expert Guidance</span>
              </div>
              <h2
                style={{ fontFamily: "'Montserrat', sans-serif" }}
                className="text-3xl md:text-4xl font-bold text-white mb-6"
              >
                Expert Advice to Achieve Your <span className="text-[#48d480]">Funding Goals</span>
              </h2>
              <p
                style={{ fontFamily: "'Inter', sans-serif" }}
                className="text-white/70 text-lg leading-relaxed mb-8"
              >
                Let our experts help you create the perfect plan to achieve your funding goals, regardless of how big or small. Get personalized strategies tailored to your business needs.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  "Personalized funding strategy session",
                  "Credit analysis and improvement plan",
                  "Access to exclusive lender relationships",
                  "Ongoing support throughout your journey"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle size={20} className="text-[#48d480]" />
                    <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/80">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
              >
                Schedule Your Free Strategy Session <ArrowRight size={16} />
              </Link>
            </div>
            
            <div className="fade-up">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="text-center mb-6">
                  <Award size={48} className="text-[#48d480] mx-auto mb-4" />
                  <h3
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                    className="text-2xl font-bold text-white mb-2"
                  >
                    Free Funding Strategy Session
                  </h3>
                  <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60">
                    No obligation, no pressure — just expert guidance
                  </p>
                </div>
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#48d480] flex items-center justify-center flex-shrink-0">
                      <Zap size={18} className="text-white" />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-semibold text-sm">Instant Assessment</div>
                      <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/50 text-xs">Know your options in minutes</div>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#e8821c] flex items-center justify-center flex-shrink-0">
                      <Target size={18} className="text-white" />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-semibold text-sm">Custom Plan</div>
                      <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/50 text-xs">Tailored to your business goals</div>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#48d480] flex items-center justify-center flex-shrink-0">
                      <Shield size={18} className="text-white" />
                    </div>
                    <div>
                      <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white font-semibold text-sm">No Credit Impact</div>
                      <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/50 text-xs">Soft pull only, no risk</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-14 fade-up">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">Success Stories</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h2
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-3xl md:text-4xl font-bold text-[#0f2d52] mb-4"
            >
              You're Going to <span className="text-[#48d480]">Love the Results</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-8 border border-gray-100 hover:shadow-lg transition-all fade-up"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} className="fill-[#e8821c] text-[#e8821c]" />
                  ))}
                </div>
                <p
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  className="text-gray-700 leading-relaxed mb-6 italic"
                >
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <div style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold">
                    {testimonial.author}
                  </div>
                  <div style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500 text-sm">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#0f2d52] via-[#1a4a7a] to-[#0f2d52]">
        <div className="container text-center max-w-3xl mx-auto fade-up">
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-3xl md:text-5xl font-black text-white mb-6 leading-tight"
          >
            Don't Wait — Your Business Growth
            <br />
            <span className="text-[#48d480]">Starts Now!</span>
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-white/70 text-lg mb-8 max-w-2xl mx-auto"
          >
            Take the first step toward securing the funding your business deserves. Pre-qualify today and get matched with the perfect loan option — fast, easy, and risk-free.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.trulli.ai/marketplace/legacytriangle"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary flex items-center justify-center gap-2 text-base px-8 py-4"
            >
              Get Started Today <ArrowRight size={18} />
            </a>
            <Link
              to="/contact"
              className="btn-outline-white flex items-center justify-center gap-2 text-base px-8 py-4"
            >
              Free Funding Strategy Session
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
